import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure
} from "@chakra-ui/react";
import { RiMenu2Fill } from "react-icons/ri";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import { obterPlanoDiretorNoPonto } from "store/modules/consultaprevia/actions/obterPlanoDiretorNoPonto";
import { ResultadoPonto } from "pages/ConsultaPrevia/components/ResultadoPonto";

interface ConsultaPreviaButtonProps {
  tipo: string;
  /** Texto da resposta do campo de mapa, de onde sai a coordenada. */
  resposta?: string;
}

type Coordenada = { latitude: number; longitude: number };

/**
 * A resposta de um campo de mapa é gravada como texto livre e o formato variou
 * ao longo do tempo (objeto serializado ou par "lat, lng"). Aceitar os dois é
 * mais barato que migrar o histórico das solicitações já protocoladas.
 */
const extrairCoordenada = (valor?: string): Coordenada | null => {
  if (!valor) return null;

  try {
    const objeto = JSON.parse(valor);
    const latitude = objeto?.lat ?? objeto?.latitude;
    const longitude = objeto?.lng ?? objeto?.lon ?? objeto?.longitude;

    if (typeof latitude === "number" && typeof longitude === "number") {
      return { latitude, longitude };
    }
  } catch {
    /* não era JSON: cai no formato textual abaixo */
  }

  const numeros = valor.match(/-?\d+[.,]?\d*/g);

  if (numeros && numeros.length >= 2) {
    const latitude = Number(numeros[0].replace(",", "."));
    const longitude = Number(numeros[1].replace(",", "."));

    if (!Number.isNaN(latitude) && !Number.isNaN(longitude)) {
      return { latitude, longitude };
    }
  }

  return null;
};

export const ConsultaPreviaButton = ({
  tipo,
  resposta
}: ConsultaPreviaButtonProps) => {
  const dispatch = useAppDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { isRequesting, successPayload, errors } = useTypedSelector(
    (state) => state.consultaprevia.obterPlanoDiretorNoPonto
  );

  const isLocationField = tipo?.toLowerCase().includes("map");
  const coordenada = extrairCoordenada(resposta);

  // Sem coordenada não há o que consultar: o botão some em vez de abrir um
  // modal vazio.
  if (!isLocationField || !coordenada) return null;

  const abrir = () => {
    dispatch(obterPlanoDiretorNoPonto(coordenada));
    onOpen();
  };

  return (
    <>
      <Button
        aria-label="Ver consulta prévia do plano diretor"
        size="xs"
        leftIcon={<RiMenu2Fill />}
        onClick={abrir}
        flexShrink={0}
      >
        Consulta Prévia
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Consulta Prévia</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <ResultadoPonto
              isRequesting={isRequesting}
              resultado={successPayload}
              errors={errors}
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onClose}>
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
